import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize, Observable, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProfetPortalApi } from '../api/profet-portal.api';
import { ConfirmDialogComponent, ConfirmDialogConfig } from '../components/confirm-dialog/confirm-dialog.component';
import { billingDialogConfigArray } from '../config/billing.config';
import { BillingApiResponse, BillingDialogConfig } from '../interface/billing.interface';
import { CommonApiResponse } from '../interface/common.interface';
import { UserRole } from '../interface/user.interface';
import { ProgressLoaderService } from './progress-loader.service';
import { SnackBarService } from './snack-bar.service';

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  billingDialogConfigArray = billingDialogConfigArray;

  userRole = UserRole;

  constructor(
    private profetPortalApi: ProfetPortalApi,
    private progressLoaderService: ProgressLoaderService,
    private snackBarService: SnackBarService,
    private _dialog: MatDialog
  ) {}

  checkBillingEligibility(reportType: string): Observable<CommonApiResponse<BillingApiResponse>> {
    const loaderId = this.progressLoaderService.show('Checking Eligibility');
    return this.profetPortalApi
      .checkBillingEligibility(reportType)
      .pipe(finalize(() => this.progressLoaderService.hide(loaderId)));
  }

  getBillingDialogConfigFromApiResponse(
    apiResponse: CommonApiResponse<BillingApiResponse>
  ): BillingDialogConfig | undefined {
    const type = apiResponse.type;
    const data = apiResponse.data || {};
    const billingModel = data?.orderTypeBillingModel;

    if (type === 'NO_BILLING_MODEL') return this.findConfig('NO_BILLING_MODEL');
    if (type === 'NO_PAYMENT_METHOD') return this.findConfig('NO_PAYMENT_METHOD');
    if (type === 'UNKNOWN_ORDER_TYPE') return this.findConfig('UNKNOWN_ORDER_TYPE');

    if (type === 'OK' || type === 'TRIAL_ORDER') {
      if (billingModel === 'CONTRACTUAL') return this.findConfig('CONTRACTUAL');

      if (billingModel === 'PAYASYOUGO' && data?.orderTypeInfo) {
        return this.findConfig('PAYASYOUGO_RECOGNIZED');
      }

      if (billingModel === 'MONTHLY') {
        const isCovered = data?.orderTypeInfo?.coveredInPlan;
        const usageCompleted =
          (data?.orderTypeInfo?.planInfo?.currentUsage ?? 0) >= (data?.orderTypeInfo?.planInfo?.includedVolume ?? 0);

        if (isCovered && !usageCompleted) {
          return this.findConfig('MONTHLY_COVERED');
        } else if (isCovered && usageCompleted) {
          return this.findConfig('MONTHLY_USAGE_EXHAUSTED_NO_PAYMENT');
        } else {
          return this.findConfig('MONTHLY_NOT_COVERED');
        }
      }
    }

    return undefined;
  }

  findConfig(responseType: string): BillingDialogConfig | undefined {
    return billingDialogConfigArray.find((c) => c.responseType === responseType);
  }

  /** Tells the user their company's payment method was declined and offers to add a new one. */
  checkPaymentStatus(companyId: string | undefined) {
    const config: ConfirmDialogConfig<unknown> = {
      secondaryButtonLabel: 'Cancel',
      primaryButtonLabel: {
        label: 'Add Payment Method',
        roles: [this.userRole.COMPANY_ADMIN, this.userRole.APP_ADMIN],
      },
      description: `Payment method declined for the current company. `,
      title: 'Payment Failed',
      actionMessage: {
        message: `Please contact your company's administrator.`,
        roles: [
          this.userRole.APPRAISER,
          this.userRole.APPRAISAL_ASSISTANT,
          this.userRole.AMC_MANAGER,
          this.userRole.MANAGER,
          this.userRole.BROKER,
          this.userRole.CHIEF_APPRAISER,
          this.userRole.INSPECTOR,
          this.userRole.REVIEWER,
          this.userRole.SUPERVISORY_APPRAISER,
        ],
      },
    };

    const dialogRef = this._dialog.open<ConfirmDialogComponent>(ConfirmDialogComponent, {
      width: '420px',
      data: config,
    });

    // The dialog owns the subscription lifetime - this service is a root singleton and is never destroyed.
    dialogRef.componentInstance.action.pipe(takeUntil(dialogRef.afterClosed())).subscribe((result) => {
      if (!result || !result.submitted) {
        dialogRef.close();
      } else if (companyId) {
        window.location.href = `${environment.portal.frontendBaseUrl}/app/addCompany/payment/${companyId}`;
      } else {
        this.snackBarService.open('error', 'Company details are unavailable. Please contact your administrator.');
        dialogRef.close();
      }
    });
  }
}
